import React from 'react';
import { Typography, Box, Tabs, Tab, Divider, Chip } from '@mui/material';
import {
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    LinkedIn as LinkedInIcon,
    GitHub as GitHubIcon,
    Language as WebIcon,
} from '@mui/icons-material';

function Preview({ formData, template, setTemplate }) {
    const handleTemplateChange = (_, newValue) => {
        setTemplate(newValue);
    };

    return (
        <Box>
            {/* Template Selector */}
            <Box className="template-selector" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Choose Template
                </Typography>
                <Tabs
                    value={template}
                    onChange={handleTemplateChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 40,
                        '& .MuiTabs-indicator': {
                            background: 'linear-gradient(135deg, #7c4dff, #448aff)',
                            borderRadius: 2,
                            height: 3,
                        },
                    }}
                >
                    <Tab className="template-tab" label="Classic" value="classic" />
                    <Tab className="template-tab" label="Modern" value="modern" />
                    <Tab className="template-tab" label="Minimal" value="minimal" />
                    <Tab className="template-tab" label="Executive" value="executive" />
                    <Tab className="template-tab" label="Professional" value="professional" />
                    <Tab className="template-tab" label="Creative" value="creative" />
                </Tabs>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Template Renders */}
            {template === 'classic' && <ClassicTemplate formData={formData} />}
            {template === 'modern' && <ModernTemplate formData={formData} />}
            {template === 'minimal' && <MinimalTemplate formData={formData} />}
            {template === 'executive' && <ExecutiveTemplate formData={formData} />}
            {template === 'professional' && <ProfessionalTemplate formData={formData} />}
            {template === 'creative' && <CreativeTemplate formData={formData} />}
        </Box>
    );
}

/* ===== Classic Template ===== */
function ClassicTemplate({ formData }) {
    return (
        <Box className="template-classic">
            {formData.photo && (
                <Box textAlign="center" mb={2}>
                    <img src={formData.photo} alt="Profile" className="photo-preview" />
                </Box>
            )}
            <Typography className="resume-name" variant="h5" align="center">
                {formData.name || 'Your Name'}
            </Typography>
            <Box textAlign="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                    {[formData.email, formData.phone, formData.address].filter(Boolean).join('  |  ')}
                </Typography>
            </Box>
            {[formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean).length > 0 && (
                <Box textAlign="center" mb={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {[formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean).join('  |  ')}
                    </Typography>
                </Box>
            )}

            {formData.summary && (
                <Box mb={2}>
                    <SectionTitle title="Summary" />
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{formData.summary}</Typography>
                </Box>
            )}

            <SectionTitle title="Education" />
            {formData.education.map((edu, i) => (
                <Box key={i} mb={1}>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={600}>{edu.institution}</Typography>
                        <Typography variant="caption" color="text.secondary">{edu.startYear} - {edu.endYear}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">{edu.degree}</Typography>
                </Box>
            ))}

            <SectionTitle title="Experience" />
            {formData.experience.map((exp, i) => (
                <Box key={i} mb={1}>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={600}>{exp.company}</Typography>
                        <Typography variant="caption" color="text.secondary">{exp.startYear} - {exp.endYear}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">{exp.role}</Typography>
                </Box>
            ))}

            {formData.skills && (
                <>
                    <SectionTitle title="Skills" />
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {formData.skills.split(',').map((skill, i) => (
                            <Chip key={i} label={skill.trim()} size="small" variant="outlined" color="primary" />
                        ))}
                    </Box>
                </>
            )}

            {formData.certifications?.some(c => c.name) && (
                <>
                    <SectionTitle title="Certifications" />
                    {formData.certifications.map((cert, i) => cert.name && (
                        <Box key={i} mb={1}>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2" fontWeight={600}>{cert.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{cert.year}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">{cert.issuer}</Typography>
                        </Box>
                    ))}
                </>
            )}

            {formData.projects?.some(p => p.title) && (
                <>
                    <SectionTitle title="Projects" />
                    {formData.projects.map((proj, i) => proj.title && (
                        <Box key={i} mb={1.5}>
                            <Typography variant="body2" fontWeight={600}>{proj.title}</Typography>
                            {proj.technologies && (
                                <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                                    {proj.technologies}
                                </Typography>
                            )}
                            {proj.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {proj.description}
                                </Typography>
                            )}
                            {proj.link && (
                                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                    {proj.link}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </>
            )}

            {formData.languages && (
                <>
                    <SectionTitle title="Languages" />
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {formData.languages.split(',').map((lang, i) => (
                            <Chip key={i} label={lang.trim()} size="small" variant="outlined" color="secondary" />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
}

/* ===== Modern Template ===== */
function ModernTemplate({ formData }) {
    return (
        <Box className="template-modern" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {/* Sidebar */}
            <Box className="sidebar">
                {formData.photo && (
                    <Box textAlign="center" mb={2}>
                        <img
                            src={formData.photo}
                            alt="Profile"
                            style={{
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '3px solid rgba(255,255,255,0.5)',
                            }}
                        />
                    </Box>
                )}
                <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 2 }}>Contact</Typography>
                <Box sx={{ mt: 1, mb: 2 }}>
                    {formData.email && (
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <EmailIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                            <Typography variant="caption">{formData.email}</Typography>
                        </Box>
                    )}
                    {formData.phone && (
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <PhoneIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                            <Typography variant="caption">{formData.phone}</Typography>
                        </Box>
                    )}
                    {formData.address && (
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <LocationIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                            <Typography variant="caption">{formData.address}</Typography>
                        </Box>
                    )}
                </Box>

                {formData.skills && (
                    <>
                        <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 2 }}>Skills</Typography>
                        <Box sx={{ mt: 1, mb: 2 }}>
                            {formData.skills.split(',').map((skill, i) => (
                                <Chip
                                    key={i}
                                    label={skill.trim()}
                                    size="small"
                                    sx={{
                                        mb: 0.5,
                                        mr: 0.5,
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            ))}
                        </Box>
                    </>
                )}

                {formData.languages && (
                    <>
                        <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 2 }}>Languages</Typography>
                        <Box sx={{ mt: 1, mb: 2 }}>
                            {formData.languages.split(',').map((lang, i) => (
                                <Chip
                                    key={i}
                                    label={lang.trim()}
                                    size="small"
                                    sx={{
                                        mb: 0.5,
                                        mr: 0.5,
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        color: 'white',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            ))}
                        </Box>
                    </>
                )}

                {[formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean).length > 0 && (
                    <>
                        <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 2 }}>Links</Typography>
                        <Box sx={{ mt: 1 }}>
                            {formData.socialLinks?.linkedin && (
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                    <LinkedInIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                                    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{formData.socialLinks.linkedin}</Typography>
                                </Box>
                            )}
                            {formData.socialLinks?.github && (
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                    <GitHubIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                                    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{formData.socialLinks.github}</Typography>
                                </Box>
                            )}
                            {formData.socialLinks?.website && (
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                    <WebIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                                    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{formData.socialLinks.website}</Typography>
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </Box>

            {/* Main Content */}
            <Box className="main-content">
                <Typography variant="h5" fontWeight={700} gutterBottom>
                    {formData.name || 'Your Name'}
                </Typography>

                {formData.summary && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                        {formData.summary}
                    </Typography>
                )}

                <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 2 }}>
                    Education
                </Typography>
                {formData.education.map((edu, i) => (
                    <Box key={i} sx={{ mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                        <Typography variant="body2" fontWeight={600}>{edu.institution}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {edu.degree} | {edu.startYear} - {edu.endYear}
                        </Typography>
                    </Box>
                ))}

                <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 2, mt: 1 }}>
                    Experience
                </Typography>
                {formData.experience.map((exp, i) => (
                    <Box key={i} sx={{ mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                        <Typography variant="body2" fontWeight={600}>{exp.company}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {exp.role} | {exp.startYear} - {exp.endYear}
                        </Typography>
                    </Box>
                ))}

                {formData.certifications?.some(c => c.name) && (
                    <>
                        <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 2, mt: 1 }}>
                            Certifications
                        </Typography>
                        {formData.certifications.map((cert, i) => cert.name && (
                            <Box key={i} sx={{ mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                                <Typography variant="body2" fontWeight={600}>{cert.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {cert.issuer}{cert.year ? ` | ${cert.year}` : ''}
                                </Typography>
                            </Box>
                        ))}
                    </>
                )}

                {formData.projects?.some(p => p.title) && (
                    <>
                        <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 2, mt: 1 }}>
                            Projects
                        </Typography>
                        {formData.projects.map((proj, i) => proj.title && (
                            <Box key={i} sx={{ mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                                <Typography variant="body2" fontWeight={600}>{proj.title}</Typography>
                                {proj.technologies && (
                                    <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic', display: 'block' }}>
                                        {proj.technologies}
                                    </Typography>
                                )}
                                {proj.description && (
                                    <Typography variant="caption" color="text.secondary">{proj.description}</Typography>
                                )}
                                {proj.link && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                                        {proj.link}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </>
                )}
            </Box>
        </Box>
    );
}

/* ===== Minimal Template ===== */
function MinimalTemplate({ formData }) {
    return (
        <Box className="template-minimal">
            {formData.photo && (
                <Box mb={2}>
                    <img
                        src={formData.photo}
                        alt="Profile"
                        style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', opacity: 0.9 }}
                    />
                </Box>
            )}
            <Typography className="resume-name" color="text.primary">
                {(formData.name || 'Your Name').toUpperCase()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {[formData.email, formData.phone, formData.address].filter(Boolean).join('   ')}
            </Typography>
            {[formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean).length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block', wordBreak: 'break-all' }}>
                    {[formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean).join('   ')}
                </Typography>
            )}

            <Divider sx={{ mb: 2 }} />

            {formData.summary && (
                <>
                    <Typography variant="body2" sx={{ lineHeight: 1.8, mb: 2 }}>{formData.summary}</Typography>
                    <Divider sx={{ mb: 2 }} />
                </>
            )}

            <Typography className="section-title">Education</Typography>
            {formData.education.map((edu, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                    {edu.institution} — {edu.degree} ({edu.startYear}–{edu.endYear})
                </Typography>
            ))}

            <Typography className="section-title">Experience</Typography>
            {formData.experience.map((exp, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                    {exp.company} — {exp.role} ({exp.startYear}–{exp.endYear})
                </Typography>
            ))}

            {formData.skills && (
                <>
                    <Typography className="section-title">Skills</Typography>
                    <Typography variant="body2">{formData.skills}</Typography>
                </>
            )}

            {formData.certifications?.some(c => c.name) && (
                <>
                    <Typography className="section-title">Certifications</Typography>
                    {formData.certifications.map((cert, i) => cert.name && (
                        <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                            {cert.name} — {cert.issuer}{cert.year ? ` (${cert.year})` : ''}
                        </Typography>
                    ))}
                </>
            )}

            {formData.projects?.some(p => p.title) && (
                <>
                    <Typography className="section-title">Projects</Typography>
                    {formData.projects.map((proj, i) => proj.title && (
                        <Box key={i} sx={{ mb: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{proj.title}</Typography>
                            {proj.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                    {proj.description}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </>
            )}

            {formData.languages && (
                <>
                    <Typography className="section-title">Languages</Typography>
                    <Typography variant="body2">{formData.languages}</Typography>
                </>
            )}
        </Box>
    );
}

/* ===== Executive Template ===== */
function ExecutiveTemplate({ formData }) {
    const ExecSection = ({ title, children }) => (
        <Box sx={{ mt: 2.5, mb: 1.5 }}>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 2,
                    color: '#1a2744',
                    fontSize: '0.75rem',
                    pb: 0.5,
                    borderBottom: '2px solid #c9a84c',
                    display: 'inline-block',
                    mb: 1.5,
                }}
            >
                {title}
            </Typography>
            {children}
        </Box>
    );

    return (
        <Box className="template-executive">
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box flex={1}>
                    <Typography sx={{ fontSize: '1.8rem', fontWeight: 300, color: '#1a2744', letterSpacing: 1, fontFamily: "'Georgia', serif" }}>
                        {formData.name || 'Your Name'}
                    </Typography>
                    <Divider sx={{ borderColor: '#c9a84c', borderWidth: 1, mt: 1, mb: 1.5 }} />
                    <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                        {formData.email && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <EmailIcon sx={{ fontSize: 13, color: '#c9a84c' }} />
                                <Typography variant="caption" sx={{ color: '#555' }}>{formData.email}</Typography>
                            </Box>
                        )}
                        {formData.phone && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <PhoneIcon sx={{ fontSize: 13, color: '#c9a84c' }} />
                                <Typography variant="caption" sx={{ color: '#555' }}>{formData.phone}</Typography>
                            </Box>
                        )}
                        {formData.address && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <LocationIcon sx={{ fontSize: 13, color: '#c9a84c' }} />
                                <Typography variant="caption" sx={{ color: '#555' }}>{formData.address}</Typography>
                            </Box>
                        )}
                    </Box>
                    {[formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean).length > 0 && (
                        <Box display="flex" flexWrap="wrap" gap={2} mt={0.5}>
                            {formData.socialLinks?.linkedin && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <LinkedInIcon sx={{ fontSize: 13, color: '#c9a84c' }} />
                                    <Typography variant="caption" sx={{ color: '#555', wordBreak: 'break-all' }}>{formData.socialLinks.linkedin}</Typography>
                                </Box>
                            )}
                            {formData.socialLinks?.github && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <GitHubIcon sx={{ fontSize: 13, color: '#c9a84c' }} />
                                    <Typography variant="caption" sx={{ color: '#555', wordBreak: 'break-all' }}>{formData.socialLinks.github}</Typography>
                                </Box>
                            )}
                            {formData.socialLinks?.website && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <WebIcon sx={{ fontSize: 13, color: '#c9a84c' }} />
                                    <Typography variant="caption" sx={{ color: '#555', wordBreak: 'break-all' }}>{formData.socialLinks.website}</Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
                {formData.photo && (
                    <Box ml={2}>
                        <img
                            src={formData.photo}
                            alt="Profile"
                            style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid #c9a84c' }}
                        />
                    </Box>
                )}
            </Box>

            {formData.summary && (
                <ExecSection title="Professional Summary">
                    <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#444', fontFamily: "'Georgia', serif", fontSize: '0.85rem' }}>
                        {formData.summary}
                    </Typography>
                </ExecSection>
            )}

            <ExecSection title="Experience">
                {formData.experience.map((exp, i) => (
                    <Box key={i} mb={1.5}>
                        <Box display="flex" justifyContent="space-between" alignItems="baseline">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a2744' }}>{exp.company}</Typography>
                            <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>{exp.startYear} - {exp.endYear}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#555' }}>{exp.role}</Typography>
                    </Box>
                ))}
            </ExecSection>

            <ExecSection title="Education">
                {formData.education.map((edu, i) => (
                    <Box key={i} mb={1.5}>
                        <Box display="flex" justifyContent="space-between" alignItems="baseline">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a2744' }}>{edu.institution}</Typography>
                            <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>{edu.startYear} - {edu.endYear}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#555' }}>{edu.degree}</Typography>
                    </Box>
                ))}
            </ExecSection>

            {formData.skills && (
                <ExecSection title="Skills">
                    <Box display="flex" flexWrap="wrap" gap={0.8}>
                        {formData.skills.split(',').map((skill, i) => (
                            <Chip
                                key={i}
                                label={skill.trim()}
                                size="small"
                                sx={{ bgcolor: '#1a2744', color: '#fff', fontSize: '0.7rem', fontWeight: 500, borderRadius: '4px' }}
                            />
                        ))}
                    </Box>
                </ExecSection>
            )}

            {formData.certifications?.some(c => c.name) && (
                <ExecSection title="Certifications">
                    {formData.certifications.map((cert, i) => cert.name && (
                        <Box key={i} mb={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="baseline">
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a2744' }}>{cert.name}</Typography>
                                <Typography variant="caption" sx={{ color: '#888' }}>{cert.year}</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#555' }}>{cert.issuer}</Typography>
                        </Box>
                    ))}
                </ExecSection>
            )}

            {formData.projects?.some(p => p.title) && (
                <ExecSection title="Projects">
                    {formData.projects.map((proj, i) => proj.title && (
                        <Box key={i} mb={1.5}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a2744' }}>{proj.title}</Typography>
                            {proj.technologies && (
                                <Typography variant="caption" sx={{ color: '#c9a84c', fontStyle: 'italic' }}>{proj.technologies}</Typography>
                            )}
                            {proj.description && (
                                <Typography variant="body2" sx={{ color: '#555', mt: 0.3 }}>{proj.description}</Typography>
                            )}
                            {proj.link && (
                                <Typography variant="caption" sx={{ color: '#888', wordBreak: 'break-all' }}>{proj.link}</Typography>
                            )}
                        </Box>
                    ))}
                </ExecSection>
            )}

            {formData.languages && (
                <ExecSection title="Languages">
                    <Box display="flex" flexWrap="wrap" gap={0.8}>
                        {formData.languages.split(',').map((lang, i) => (
                            <Chip
                                key={i}
                                label={lang.trim()}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: '#c9a84c', color: '#1a2744', fontSize: '0.7rem', borderRadius: '4px' }}
                            />
                        ))}
                    </Box>
                </ExecSection>
            )}
        </Box>
    );
}

/* ===== Professional Template ===== */
function ProfessionalTemplate({ formData }) {
    const ProSection = ({ title, children }) => (
        <Box sx={{ mb: 2 }}>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1.5,
                    fontSize: '0.75rem',
                    color: '#2d3748',
                    pl: 1,
                    borderLeft: '3px solid #3182ce',
                    mb: 1,
                }}
            >
                {title}
            </Typography>
            {children}
        </Box>
    );

    return (
        <Box className="template-professional">
            {/* Header strip */}
            <Box className="prof-header">
                <Box display="flex" alignItems="center" gap={2}>
                    {formData.photo && (
                        <img
                            src={formData.photo}
                            alt="Profile"
                            style={{ width: 70, height: 70, borderRadius: '50%', objectFit: 'cover', border: '3px solid rgba(255,255,255,0.3)' }}
                        />
                    )}
                    <Box>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', letterSpacing: 0.5 }}>
                            {formData.name || 'Your Name'}
                        </Typography>
                        {formData.summary && (
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5, lineHeight: 1.6, fontSize: '0.8rem' }}>
                                {formData.summary}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Two-column body */}
            <Box display="flex" className="prof-body">
                {/* Left sidebar */}
                <Box className="prof-sidebar">
                    <ProSection title="Contact">
                        {formData.email && (
                            <Box display="flex" alignItems="center" gap={0.8} mb={0.5}>
                                <EmailIcon sx={{ fontSize: 13, color: '#3182ce' }} />
                                <Typography variant="caption">{formData.email}</Typography>
                            </Box>
                        )}
                        {formData.phone && (
                            <Box display="flex" alignItems="center" gap={0.8} mb={0.5}>
                                <PhoneIcon sx={{ fontSize: 13, color: '#3182ce' }} />
                                <Typography variant="caption">{formData.phone}</Typography>
                            </Box>
                        )}
                        {formData.address && (
                            <Box display="flex" alignItems="center" gap={0.8} mb={0.5}>
                                <LocationIcon sx={{ fontSize: 13, color: '#3182ce' }} />
                                <Typography variant="caption">{formData.address}</Typography>
                            </Box>
                        )}
                    </ProSection>

                    {formData.skills && (
                        <ProSection title="Skills">
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {formData.skills.split(',').map((skill, i) => (
                                    <Chip
                                        key={i}
                                        label={skill.trim()}
                                        size="small"
                                        sx={{ bgcolor: '#3182ce', color: '#fff', fontSize: '0.65rem', height: 22, borderRadius: '4px' }}
                                    />
                                ))}
                            </Box>
                        </ProSection>
                    )}

                    {formData.languages && (
                        <ProSection title="Languages">
                            {formData.languages.split(',').map((lang, i) => (
                                <Typography key={i} variant="caption" sx={{ display: 'block', mb: 0.3 }}>
                                    {lang.trim()}
                                </Typography>
                            ))}
                        </ProSection>
                    )}

                    {[formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean).length > 0 && (
                        <ProSection title="Links">
                            {formData.socialLinks?.linkedin && (
                                <Box display="flex" alignItems="center" gap={0.8} mb={0.5}>
                                    <LinkedInIcon sx={{ fontSize: 13, color: '#3182ce' }} />
                                    <Typography variant="caption" sx={{ wordBreak: 'break-all', fontSize: '0.65rem' }}>{formData.socialLinks.linkedin}</Typography>
                                </Box>
                            )}
                            {formData.socialLinks?.github && (
                                <Box display="flex" alignItems="center" gap={0.8} mb={0.5}>
                                    <GitHubIcon sx={{ fontSize: 13, color: '#3182ce' }} />
                                    <Typography variant="caption" sx={{ wordBreak: 'break-all', fontSize: '0.65rem' }}>{formData.socialLinks.github}</Typography>
                                </Box>
                            )}
                            {formData.socialLinks?.website && (
                                <Box display="flex" alignItems="center" gap={0.8} mb={0.5}>
                                    <WebIcon sx={{ fontSize: 13, color: '#3182ce' }} />
                                    <Typography variant="caption" sx={{ wordBreak: 'break-all', fontSize: '0.65rem' }}>{formData.socialLinks.website}</Typography>
                                </Box>
                            )}
                        </ProSection>
                    )}
                </Box>

                {/* Right main content */}
                <Box className="prof-main">
                    <ProSection title="Experience">
                        {formData.experience.map((exp, i) => (
                            <Box key={i} mb={1.5}>
                                <Box display="flex" justifyContent="space-between" alignItems="baseline">
                                    <Typography variant="body2" fontWeight={700}>{exp.company}</Typography>
                                    <Typography variant="caption" color="text.secondary">{exp.startYear} - {exp.endYear}</Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">{exp.role}</Typography>
                            </Box>
                        ))}
                    </ProSection>

                    <ProSection title="Education">
                        {formData.education.map((edu, i) => (
                            <Box key={i} mb={1.5}>
                                <Box display="flex" justifyContent="space-between" alignItems="baseline">
                                    <Typography variant="body2" fontWeight={700}>{edu.institution}</Typography>
                                    <Typography variant="caption" color="text.secondary">{edu.startYear} - {edu.endYear}</Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">{edu.degree}</Typography>
                            </Box>
                        ))}
                    </ProSection>

                    {formData.certifications?.some(c => c.name) && (
                        <ProSection title="Certifications">
                            {formData.certifications.map((cert, i) => cert.name && (
                                <Box key={i} mb={1}>
                                    <Box display="flex" justifyContent="space-between" alignItems="baseline">
                                        <Typography variant="body2" fontWeight={700}>{cert.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{cert.year}</Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">{cert.issuer}</Typography>
                                </Box>
                            ))}
                        </ProSection>
                    )}

                    {formData.projects?.some(p => p.title) && (
                        <ProSection title="Projects">
                            {formData.projects.map((proj, i) => proj.title && (
                                <Box key={i} mb={1.5}>
                                    <Typography variant="body2" fontWeight={700}>{proj.title}</Typography>
                                    {proj.technologies && (
                                        <Typography variant="caption" sx={{ color: '#3182ce', fontStyle: 'italic' }}>{proj.technologies}</Typography>
                                    )}
                                    {proj.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>{proj.description}</Typography>
                                    )}
                                    {proj.link && (
                                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{proj.link}</Typography>
                                    )}
                                </Box>
                            ))}
                        </ProSection>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

/* ===== Creative Template ===== */
function CreativeTemplate({ formData }) {
    return (
        <Box className="template-creative" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {/* Main Content (left) */}
            <Box className="creative-main">
                <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: '#2d2d2d', letterSpacing: -0.5, mb: 0.5 }}>
                    {formData.name || 'Your Name'}
                </Typography>

                {formData.summary && (
                    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7, mb: 2 }}>
                        {formData.summary}
                    </Typography>
                )}

                {/* Experience with timeline */}
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)' }} />
                        Experience
                    </Typography>
                    {formData.experience.map((exp, i) => (
                        <Box key={i} sx={{ mb: 1.5, pl: 2, borderLeft: '2px solid #e0e0e0', ml: 0.4 }}>
                            <Typography variant="body2" fontWeight={700}>{exp.company}</Typography>
                            <Typography variant="caption" sx={{ color: '#764ba2', fontWeight: 600 }}>{exp.role}</Typography>
                            <Typography variant="caption" display="block" color="text.secondary">{exp.startYear} - {exp.endYear}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Education with timeline */}
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)' }} />
                        Education
                    </Typography>
                    {formData.education.map((edu, i) => (
                        <Box key={i} sx={{ mb: 1.5, pl: 2, borderLeft: '2px solid #e0e0e0', ml: 0.4 }}>
                            <Typography variant="body2" fontWeight={700}>{edu.institution}</Typography>
                            <Typography variant="caption" sx={{ color: '#764ba2', fontWeight: 600 }}>{edu.degree}</Typography>
                            <Typography variant="caption" display="block" color="text.secondary">{edu.startYear} - {edu.endYear}</Typography>
                        </Box>
                    ))}
                </Box>

                {formData.certifications?.some(c => c.name) && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)' }} />
                            Certifications
                        </Typography>
                        {formData.certifications.map((cert, i) => cert.name && (
                            <Box key={i} sx={{ mb: 1, pl: 2, borderLeft: '2px solid #e0e0e0', ml: 0.4 }}>
                                <Typography variant="body2" fontWeight={700}>{cert.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{cert.issuer}{cert.year ? ` | ${cert.year}` : ''}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {formData.projects?.some(p => p.title) && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)' }} />
                            Projects
                        </Typography>
                        {formData.projects.map((proj, i) => proj.title && (
                            <Box key={i} sx={{ mb: 1.5, pl: 2, borderLeft: '2px solid #e0e0e0', ml: 0.4 }}>
                                <Typography variant="body2" fontWeight={700}>{proj.title}</Typography>
                                {proj.technologies && (
                                    <Typography variant="caption" sx={{ color: '#667eea', fontStyle: 'italic', display: 'block' }}>{proj.technologies}</Typography>
                                )}
                                {proj.description && (
                                    <Typography variant="caption" color="text.secondary">{proj.description}</Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Sidebar (right) */}
            <Box className="creative-sidebar">
                {formData.photo && (
                    <Box textAlign="center" mb={2}>
                        <img
                            src={formData.photo}
                            alt="Profile"
                            style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '4px solid rgba(255,255,255,0.3)' }}
                        />
                    </Box>
                )}

                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 2, fontSize: '0.65rem' }}>Contact</Typography>
                <Box sx={{ mt: 1, mb: 2 }}>
                    {formData.email && (
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <EmailIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                            <Typography variant="caption">{formData.email}</Typography>
                        </Box>
                    )}
                    {formData.phone && (
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <PhoneIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                            <Typography variant="caption">{formData.phone}</Typography>
                        </Box>
                    )}
                    {formData.address && (
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <LocationIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                            <Typography variant="caption">{formData.address}</Typography>
                        </Box>
                    )}
                </Box>

                {formData.skills && (
                    <>
                        <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 2, fontSize: '0.65rem' }}>Skills</Typography>
                        <Box sx={{ mt: 1, mb: 2 }}>
                            {formData.skills.split(',').map((skill, i) => (
                                <Chip
                                    key={i}
                                    label={skill.trim()}
                                    size="small"
                                    sx={{ mb: 0.5, mr: 0.5, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.65rem', borderRadius: '12px' }}
                                />
                            ))}
                        </Box>
                    </>
                )}

                {formData.languages && (
                    <>
                        <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 2, fontSize: '0.65rem' }}>Languages</Typography>
                        <Box sx={{ mt: 1, mb: 2 }}>
                            {formData.languages.split(',').map((lang, i) => (
                                <Chip
                                    key={i}
                                    label={lang.trim()}
                                    size="small"
                                    sx={{ mb: 0.5, mr: 0.5, bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontSize: '0.65rem', borderRadius: '12px' }}
                                />
                            ))}
                        </Box>
                    </>
                )}

                {[formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean).length > 0 && (
                    <>
                        <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 2, fontSize: '0.65rem' }}>Links</Typography>
                        <Box sx={{ mt: 1 }}>
                            {formData.socialLinks?.linkedin && (
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                    <LinkedInIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                                    <Typography variant="caption" sx={{ wordBreak: 'break-all', fontSize: '0.65rem' }}>{formData.socialLinks.linkedin}</Typography>
                                </Box>
                            )}
                            {formData.socialLinks?.github && (
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                    <GitHubIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                                    <Typography variant="caption" sx={{ wordBreak: 'break-all', fontSize: '0.65rem' }}>{formData.socialLinks.github}</Typography>
                                </Box>
                            )}
                            {formData.socialLinks?.website && (
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                    <WebIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                                    <Typography variant="caption" sx={{ wordBreak: 'break-all', fontSize: '0.65rem' }}>{formData.socialLinks.website}</Typography>
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
}

/* ===== Shared Components ===== */
function SectionTitle({ title }) {
    return (
        <Box sx={{ mt: 2, mb: 1 }}>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1.5,
                    color: 'primary.main',
                    fontSize: '0.8rem',
                }}
            >
                {title}
            </Typography>
            <Divider sx={{ mt: 0.5 }} />
        </Box>
    );
}

export default Preview;
